import makeCrud from '@/store/site/localization/crud'

// Trendyol pazaryeri entegrasyonu store'u.
//
// Liste ekranları ortak `makeCrud` fabrikasıyla (server-side sayfalama/filtre)
// alt modüllere ayrılır; aksiyonlar (gönder, senkronla, statü bildir) kök
// modülde toplanır — hepsi tekil uçlardır, tablo state'i tutmazlar.

const state = () => ({
    dashboard: null,
    settings: null,
})

const getters = {}

const actions = {
    // ── Ayarlar ──────────────────────────────────────────────────────────
    async fetchSettings({commit}) {
        const res = await this._vm.axios.get(this._vm.endpoint.get('ecommerce_trendyol_setting'))
        const data = res?.data?.data ?? null
        commit('setSettings', data)
        return data
    },
    async saveSettings(_, {data}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_setting'), data)
        return res?.data
    },
    async testConnection() {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_setting_test'))
        return res?.data
    },
    async fetchDashboard({commit}) {
        const res = await this._vm.axios.get(this._vm.endpoint.get('ecommerce_trendyol_dashboard'))
        const data = res?.data?.data ?? null
        commit('setDashboard', data)
        return data
    },

    // ── Referans veriler (Trendyol kategori/marka/adres) ─────────────────
    async fetchTrendyolCategories(_, {flat = true, fresh = false} = {}) {
        const res = await this._vm.axios.get(this._vm.endpoint.get('ecommerce_trendyol_lookup_category'), {
            params: {flat: flat ? 'true' : 'false', fresh: fresh ? 'true' : undefined},
        })
        return res?.data?.data ?? []
    },
    async fetchCategoryAttributes(_, {categoryId, fresh = false}) {
        const res = await this._vm.axios.get(
            this._vm.endpoint.get('ecommerce_trendyol_lookup_category') + '/' + categoryId + '/attribute',
            {params: {fresh: fresh ? 'true' : undefined}}
        )
        return res?.data?.data ?? {}
    },
    async fetchAttributeValues(_, {categoryId, attributeId}) {
        const res = await this._vm.axios.get(
            this._vm.endpoint.get('ecommerce_trendyol_lookup_category') + '/' + categoryId + '/attribute/' + attributeId + '/value'
        )
        return res?.data?.data ?? []
    },
    async searchTrendyolBrands(_, {name}) {
        const res = await this._vm.axios.get(this._vm.endpoint.get('ecommerce_trendyol_lookup_brand'), {params: {name}})
        return res?.data?.data ?? []
    },
    async fetchAddresses(_, {fresh = false} = {}) {
        const res = await this._vm.axios.get(this._vm.endpoint.get('ecommerce_trendyol_lookup_address'), {
            params: {fresh: fresh ? 'true' : undefined},
        })
        return res?.data?.data ?? []
    },

    // ── Eşleştirme ───────────────────────────────────────────────────────
    async saveCategoryMapping(_, {id, data}) {
        const base = this._vm.endpoint.get('ecommerce_trendyol_mapping_category')
        const res = id
            ? await this._vm.axios.put(base + '/' + id, data)
            : await this._vm.axios.post(base, data)
        return res?.data
    },
    async saveBrandMapping(_, {id, data}) {
        const base = this._vm.endpoint.get('ecommerce_trendyol_mapping_brand')
        const res = id
            ? await this._vm.axios.put(base + '/' + id, data)
            : await this._vm.axios.post(base, data)
        return res?.data
    },

    // ── Ürün aksiyonları ─────────────────────────────────────────────────
    async pushProducts(_, {productIds}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_product') + '/push', {product_ids: productIds})
        return res?.data
    },
    async refreshProducts() {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_product') + '/refresh')
        return res?.data
    },
    async pushInventory(_, {linkIds = null, force = false} = {}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_product') + '/inventory', {
            link_ids: linkIds,
            force,
        })
        return res?.data
    },
    async archiveProducts(_, {linkIds, archived = true}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_product') + '/archive', {
            link_ids: linkIds,
            archived,
        })
        return res?.data
    },
    async deleteProducts(_, {linkIds}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_product') + '/delete', {link_ids: linkIds})
        return res?.data
    },
    async previewProduct(_, {productId}) {
        const res = await this._vm.axios.get(this._vm.endpoint.get('ecommerce_trendyol_product') + '/preview/' + productId)
        return res?.data?.data
    },

    // ── Sipariş aksiyonları ──────────────────────────────────────────────
    async syncOrders(_, payload = {}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_order') + '/sync', payload)
        return res?.data
    },
    async importOrder(_, {id}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_order') + '/' + id + '/import')
        return res?.data
    },
    async updateOrderStatus(_, {id, status, invoiceNumber = null}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_order') + '/' + id + '/status', {
            status,
            invoice_number: invoiceNumber,
        })
        return res?.data
    },
    async markUnsupplied(_, {id, reasonId = 500}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_order') + '/' + id + '/unsupplied', {
            reason_id: reasonId,
        })
        return res?.data
    },
    async sendInvoiceLink(_, {id, invoiceLink, invoiceNumber = null}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_order') + '/' + id + '/invoice-link', {
            invoice_link: invoiceLink,
            invoice_number: invoiceNumber,
        })
        return res?.data
    },
    async ignoreOrder(_, {id}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_order') + '/' + id + '/ignore')
        return res?.data
    },

    // ── İade aksiyonları ─────────────────────────────────────────────────
    async syncClaims(_, payload = {}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_claim') + '/sync', payload)
        return res?.data
    },
    async approveClaim(_, {id}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_claim') + '/' + id + '/approve')
        return res?.data
    },

    // ── Loglar ───────────────────────────────────────────────────────────
    async checkBatches() {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_batch') + '/check')
        return res?.data
    },
    async pruneLogs(_, {days = 30} = {}) {
        const res = await this._vm.axios.post(this._vm.endpoint.get('ecommerce_trendyol_log') + '/prune', {days})
        return res?.data
    },
}

const mutations = {
    setDashboard(state, data) {
        state.dashboard = data
    },
    setSettings(state, data) {
        state.settings = data
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
    modules: {
        // Liste ekranları — hepsi ortak CRUD fabrikası (server-side sayfalama)
        categoryMapping: makeCrud('ecommerce_trendyol_mapping_category'),
        brandMapping: makeCrud('ecommerce_trendyol_mapping_brand'),
        product: makeCrud('ecommerce_trendyol_product'),
        candidate: makeCrud('ecommerce_trendyol_product_candidate'),
        order: makeCrud('ecommerce_trendyol_order'),
        claim: makeCrud('ecommerce_trendyol_claim'),
        batch: makeCrud('ecommerce_trendyol_batch'),
        log: makeCrud('ecommerce_trendyol_log'),
    },
}
